'use client';

import { c as _c } from "react/compiler-runtime";
import { css, cx } from '@repo/panda/css';
import type React from 'react';
import { useReducedMotion } from '../hooks/useReducedMotion';

/** Matches desktop Aurelia pulse / Discord connected indicator (`#03f570`). */
const DEFAULT_DOT_COLOR = 'rgba(3, 245, 112, 0.92)';
/** Softer expanding ring from the same Aurelia mixin usage. */
const DEFAULT_PULSE_COLOR = 'rgba(2, 191, 87, 0.38)';
export interface LivePulseDotProps {
  /** Outer box size in pixels. Defaults to 8. */
  size?: number;
  className?: string;
  /** Core dot fill. Defaults to Wand pro green. */
  color?: string;
  /** Expanding ring fill. Defaults to a softer green. */
  pulseColor?: string;
}
const layerStyles = css({
  position: 'absolute',
  inset: '0',
  borderRadius: 'full'
});

/**
 * Pulsing green "live" indicator.
 * Ported from the desktop Aurelia `@mixin pulse` in `packages/aurelia-shared/styles/pulse.scss`.
 */
export const LivePulseDot: React.FC<LivePulseDotProps> = t0 => {
  const $ = _c(22);
  const {
    size: t1,
    className,
    color: t2,
    pulseColor: t3
  } = t0;
  const size = t1 === undefined ? 8 : t1;
  const color = t2 === undefined ? DEFAULT_DOT_COLOR : t2;
  const pulseColor = t3 === undefined ? DEFAULT_PULSE_COLOR : t3;
  const prefersReducedMotion = useReducedMotion();
  let t4;
  if ($[0] !== className) {
    t4 = cx(css({
      position: "relative",
      display: "inline-block",
      flexShrink: "0"
    }), className);
    $[0] = className;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== size) {
    t5 = {
      width: size,
      height: size
    };
    $[2] = size;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== prefersReducedMotion || $[5] !== pulseColor) {
    t6 = !prefersReducedMotion && <span className={cx(layerStyles, css({
      animationName: "live-pulse-ring",
      animationDuration: "[1.5s]",
      animationIterationCount: "infinite"
    }))} style={{
      background: pulseColor
    }} />;
    $[4] = prefersReducedMotion;
    $[5] = pulseColor;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== prefersReducedMotion) {
    t7 = cx(layerStyles, !prefersReducedMotion && css({
      animationName: "live-pulse-dot",
      animationDuration: "[1.5s]",
      animationTimingFunction: "[ease-in-out]",
      animationIterationCount: "infinite"
    }));
    $[7] = prefersReducedMotion;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== prefersReducedMotion) {
    t8 = prefersReducedMotion ? {
      transform: "scale(0.5)"
    } : undefined;
    $[9] = prefersReducedMotion;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== color || $[12] !== t8) {
    t9 = {
      background: color,
      ...t8
    };
    $[11] = color;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t7 || $[15] !== t9) {
    t10 = <span className={t7} style={t9} />;
    $[14] = t7;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10 || $[18] !== t4 || $[19] !== t5 || $[20] !== t6) {
    t11 = <span className={t4} style={t5} aria-hidden="true">{t6}{t10}</span>;
    $[17] = t10;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  return t11;
};